// components/MainGrid.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import {
    FaSmile,
    FaMeh,
    FaFrown,
    FaSadTear,
    FaChevronLeft,
    FaChevronRight,
    FaEllipsisH,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useFilter } from "../contexts/FilterContext";
import Tools from "../utils/Tools";

// Custom Header Component with styled tooltips
const CustomHeaderComponent = (props) => {
    const tooltipContent = props.column.getColDef().tooltipContent;
    const isSortable = props.column.getColDef().sortable !== false; // Check if column is sortable

    const onSortRequested = (event) => {
        // Don't sort if the column isn't sortable
        if (!isSortable) return;

        // Get current sort state
        const currentSort = props.column.getSort();

        // Toggle between asc, desc, and null (no sort)
        let nextSort;
        if (currentSort === 'asc') {
            nextSort = 'desc';
        } else if (currentSort === 'desc') {
            nextSort = null;
        } else {
            nextSort = 'asc';
        }

        props.setSort(nextSort, event.shiftKey);
    };

    // Get sort icon based on current state - only show if sortable
    let sortIcon = null;
    if (isSortable) {
        if (props.column.isSortAscending()) {
            sortIcon = <span className="ag-header-icon ag-sort-ascending-icon">↑</span>;
        } else if (props.column.isSortDescending()) {
            sortIcon = <span className="ag-header-icon ag-sort-descending-icon">↓</span>;
        }
    }

    // Add different cursor style based on sortability
    const cursorStyle = isSortable ? "cursor-pointer" : "cursor-default";

    if (!tooltipContent) {
        return (
            <div className={`ag-header-cell-label ${cursorStyle}`} onClick={onSortRequested}>
                <span className="ag-header-cell-text">{props.displayName}</span>
                {sortIcon}
            </div>
        );
    }

    return (
        <Tippy content={tooltipContent}
            placement="bottom"
            arrow={true}
            duration={[200, 200]}
            className="custom-tooltip"
            appendTo={document.body}
            interactive={true}>
            <div className={`ag-header-cell-label flex items-center ${cursorStyle}`} onClick={onSortRequested}>
                <span className="ag-header-cell-text">{props.displayName}</span>
                {sortIcon}
            </div>
        </Tippy>
    );
};

function MainGrid({
    data,
    currentPage,
    totalPages,
    totalRecs,
    onPageChange,
    onSortChange,
    sortModel,
    hiddenFields = [],
    widthOverrides = {},
    isQualityMangament = false,
    selectedAgent = null,
    selectedIntent = null
}) {
    const { filters } = useFilter();
    const navigate = useNavigate();
    const location = useLocation();
    const gridApiRef = useRef(null);
    const currentSortModelRef = useRef(sortModel);
    const isUpdatingRef = useRef(false);
    const [isExporting, setIsExporting] = useState(false);

    const qualityManagementEnabled = window.appSettings?.qualityManagementEnabled;

    const formatNumericValue = (value, decimals = 2) => {
        if (value == null || isNaN(parseFloat(value))) {
            return "N/A";
        }
        return parseFloat(value).toFixed(decimals);
    };


    const QMAutofailRenderer = (params) => {
        if (!params.value) return null;

        const contactId = params.data.contact_id;
        if (!contactId) {
            // Fallback for safety, though should not happen if autofail exists
            let tooltipContent = "This call autofailed!";
            let color = "text-red-600";
            return (
                <Tippy
                    content={tooltipContent}
                    className="custom-tooltip"
                    duration={[200, 400]}
                    delay={100}
                >
                    <span className={color}>FAIL</span>
                </Tippy>
            );
        }

        const handleContextMenu = (event) => {
            event.preventDefault();

            // Remove any existing menu to prevent multiple menus
            const existingMenu = document.getElementById("custom-menu");
            if (existingMenu) {
                document.body.removeChild(existingMenu);
            }

            // Create the menu
            const menu = document.createElement("div");
            menu.id = "custom-menu"; // Add an ID to easily reference it later
            menu.style.position = "absolute";
            menu.style.left = `${event.pageX}px`;
            menu.style.top = `${event.pageY}px`;
            menu.style.border = "1px solid #ccc";
            menu.style.padding = "8px";
            menu.style.zIndex = 1000;
            menu.style.backgroundColor = "white";
            menu.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
            menu.style.borderRadius = "4px";

            // Create the "Open in New Window" option
            const openInNewWindowOption = document.createElement("div");
            openInNewWindowOption.textContent = "Open in New Window";
            openInNewWindowOption.style.cursor = "pointer";
            openInNewWindowOption.style.padding = "4px 8px";
            openInNewWindowOption.onmouseover = () => {
                openInNewWindowOption.style.backgroundColor = "#f3f4f6";
            };
            openInNewWindowOption.onmouseout = () => {
                openInNewWindowOption.style.backgroundColor = "transparent";
            };
            openInNewWindowOption.onclick = () => {
                window.open(`/scorecard/${contactId}`, "_blank");
                if (menu.parentNode) {
                    menu.parentNode.removeChild(menu);
                }
            };

            menu.appendChild(openInNewWindowOption);
            document.body.appendChild(menu);

            // Remove the menu when clicking outside
            const handleClickOutside = (event) => {
                if (menu && !menu.contains(event.target)) {
                    if (menu.parentNode) {
                        menu.parentNode.removeChild(menu);
                    }
                    document.removeEventListener("click", handleClickOutside);
                }
            };

            document.addEventListener("click", handleClickOutside);
        };

        return (
            <div className="flex justify-center items-center h-10 w-full">
                <span
                    className="text-red-600 font-medium text-sm cursor-pointer hover:underline"
                    onClick={() => {
                        navigate(`/scorecard/${contactId}`, {
                            state: { filters: filters, referrer: location.pathname },
                        });
                    }}
                    onContextMenu={handleContextMenu}
                >
                    FAIL
                </span>
            </div>
        );
    };


    const getSentimentIcon = (sentiment) => {
        const sentimentValue = parseFloat(sentiment);

        let icon;
        let color;


        if (isNaN(sentimentValue)) {
            icon = <FaMeh size={24} />;
            color = "text-gray-300";
        } else if (sentimentValue > 0.75) {
            icon = <FaSmile size={24} />;
            color = "text-green-400";
        } else if (sentimentValue > 0.25) {
            icon = <FaSmile size={24} />;
            color = "text-green-200";
        } else if (sentimentValue >= -0.25) {
            icon = <FaMeh size={24} />;
            color = "text-gray-300";
        } else if (sentimentValue >= -0.75) {
            icon = <FaFrown size={24} />;
            color = "text-red-300";
        } else {
            icon = <FaSadTear size={24} />;
            color = "text-red-500";
        }

        const tooltipContent = (
            <div>
                <div className="tooltip-title">Sentiment Score</div>
                <div>{isNaN(sentimentValue) ? "Unknown" : formatNumericValue(sentimentValue)}</div>
                <div className="tooltip-range">Valid range: -1 to 1</div>
            </div>
        );

        return (
            <Tippy
                content={tooltipContent}
                className="custom-tooltip"
                duration={[200, 400]}
                delay={100}
            >
                <span className={color}>{icon}</span>
            </Tippy>
        );
    };

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds) % 60;

        let result = "";

        if (hours > 0) {
            result += hours + "h";
        }

        if (minutes > 0) {
            result += minutes + "m";
        }

        if (remainingSeconds > 0 || (minutes === 0 && remainingSeconds === 0)) {
            result += remainingSeconds + "s";
        }

        return result;
    };

    const getCustomerSatisfaction = (score, tipText) => {
        let text, color;
        if (score === null || score === undefined) {
            text = "UNKNOWN";
            color = "text-gray-500";
        } else if (score >= 0.8) {
            text = "VERY SATISFIED";
            color = "text-green-600";
        } else if (score >= 0.6) {
            text = "SATISFIED";
            color = "text-green-500";
        } else if (score >= 0.4) {
            text = "NEUTRAL";
            color = "text-gray-400";
        } else if (score >= 0.2) {
            text = "DISSATISFIED";
            color = "text-red-400";
        } else {
            text = "VERY DISSATISFIED";
            color = "font-semibold text-red-700";
        }

        const tooltipContent = (
            <div>
                <div className="tooltip-title">Customer Satisfaction</div>
                <div>{text} ({formatNumericValue(score)})</div>


                <div className="tooltip-range">Valid range: 0 to 1</div>
                {tipText && <div className="tooltip-description">{tipText}</div>}
            </div>
        );

        return (
            <Tippy
                content={tooltipContent}
                className="custom-tooltip"
                duration={[200, 400]}
                delay={100}
            >
                <span className={color}>{text}</span>
            </Tippy>
        );
    };


    const formatDate = (dateStr) => {
        let newVal = new Date(dateStr).toLocaleString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
            timeZoneName: "short",
        });
        //console.log("Got date:", dateStr, newVal);
        return newVal;
    };

    const IntentCellRenderer = (props) => {
        if (!props.value) return null;

        // Create tooltip content with styled labels
        const tooltipContent = (
            <div>
                <div className="tooltip-title">Intents</div>
                {props.value.map((intent, index) => (
                    <div key={index} className="mb-2">
                        <div>
                            <span className="font-medium text-blue-300">Category: </span>
                            <span>{intent.category}</span>
                        </div>
                        <div>
                            <span className="font-medium text-blue-300">Major Intent: </span>
                            <span>{intent.major_intent || intent.minor_intent}</span>
                        </div>
                        <div>
                            <span className="font-medium text-blue-300">Minor Intent: </span>
                            <span>{intent.minor_intent}</span>
                        </div>
                        {index < props.value.length - 1 && <hr className="border-gray-600 my-2" />}
                    </div>
                ))}
            </div>
        );

        return (
            <Tippy
                content={tooltipContent}
                interactive={true}
                appendTo={document.body}
                placement="top"
                maxWidth={400}
                className="custom-tooltip"
                duration={[200, 400]}
                delay={100}
            >
                <div className="cursor-pointer">
                    {props.value.map((intent, index) => (
                        <span
                            key={index}
                            className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded inline-block"
                        >
                            {intent.major_intent || intent.minor_intent}
                        </span>
                    ))}
                </div>
            </Tippy>
        );
    };

    const ScoreProgressBar = ({ value, reason, color = "blue" }) => {
        const tooltipContent = (
            <div>
                <div className="tooltip-title">Score: {formatNumericValue(value)}</div>
                <div className="tooltip-range">Valid range: 0 to 1</div>
                {reason && <div className="tooltip-description">{reason}</div>}
            </div>
        );

        return (
            <Tippy
                content={tooltipContent}
                className="custom-tooltip"
                duration={[200, 400]}
                delay={100}
            >
                {value === null || value === undefined ? (
                    <span className="text-gray-500 ml-2">N/A</span>
                ) : (
                    <div className="flex items-center justify-center w-full h-full py-4">
                        <div className="bg-gray-200 w-4/5 h-3 rounded-full">
                            <div
                                className={`bg-${color}-500 h-full rounded-full`}
                                style={{ width: `${value * 100}%` }}
                            ></div>
                        </div>
                    </div>
                )}
            </Tippy>
        );
    };

    const baseColumnDefs = [
        {
            headerName: "Contact ID",
            field: "contact_id",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Contact ID</div>
                    <div className="tooltip-description">Unique identifier for the contact record</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            cellRenderer: (params) => {
                const handleContextMenu = (event) => {
                    event.preventDefault();

                    // Remove any existing menu to prevent multiple menus
                    const existingMenu = document.getElementById("custom-menu");
                    if (existingMenu) {
                        document.body.removeChild(existingMenu);
                    }

                    // Create the menu
                    const menu = document.createElement("div");
                    menu.id = "custom-menu"; // Add an ID to easily reference it later
                    menu.style.position = "absolute";
                    menu.style.left = `${event.pageX}px`;
                    menu.style.top = `${event.pageY}px`;
                    menu.style.border = "1px solid #ccc";
                    menu.style.padding = "8px";
                    menu.style.zIndex = 1000;
                    menu.style.backgroundColor = "white";
                    menu.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
                    menu.style.borderRadius = "4px";

                    // Create the "Open in New Window" option
                    const openInNewWindowOption = document.createElement("div");
                    openInNewWindowOption.textContent = "Open in New Window";
                    openInNewWindowOption.style.cursor = "pointer";
                    openInNewWindowOption.style.padding = "4px 8px";
                    openInNewWindowOption.onmouseover = () => {
                        openInNewWindowOption.style.backgroundColor = "#f3f4f6";
                    };
                    openInNewWindowOption.onmouseout = () => {
                        openInNewWindowOption.style.backgroundColor = "transparent";
                    };
                    openInNewWindowOption.onclick = () => {
                        window.open(`/contact/${params.value}`, "_blank");
                        if (menu.parentNode) {
                            menu.parentNode.removeChild(menu);
                        }
                    };

                    menu.appendChild(openInNewWindowOption);
                    document.body.appendChild(menu);

                    // Remove the menu when clicking outside
                    const handleClickOutside = (event) => {
                        if (menu && !menu.contains(event.target)) {
                            if (menu.parentNode) {
                                menu.parentNode.removeChild(menu);
                            }
                            document.removeEventListener("click", handleClickOutside);
                        }
                    };

                    document.addEventListener("click", handleClickOutside);
                };

                return (
                    <span
                        className="text-usan-primary hover:text-usan-primary cursor-pointer"
                        onContextMenu={handleContextMenu}
                        onClick={() => {
                            navigate(`/contact/${params.value}`, {
                                state: { filters: filters, referrer: location.pathname },
                            });
                        }}
                    >
                        {params.value}
                    </span>
                );
            },
            width: 110,
        },
        {
            headerName: "Date",
            field: "call_date",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Call Date & Time</div>
                    <div className="tooltip-description">When the call was recorded</div>
                    <div className="tooltip-range">Displayed in your timezone</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            valueFormatter: (params) => formatDate(params.value),
            width: 220,
        },
        {
            headerName: "Duration",
            field: "duration",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Call Duration</div>
                    <div className="tooltip-description">Total length of the call</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            valueFormatter: (params) => formatTime(params.value),
            width: 100,
        },
        {
            headerName: "Agent Name",
            field: "agent",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Agent Name</div>
                    <div className="tooltip-description">Name of the customer service representative</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            width: 160,
            cellRenderer: (params) => (
                <Tippy content={params.value} className="custom-tooltip" duration={[200, 400]}
                    delay={100}>
                    <span>{params.value}</span>
                </Tippy>
            ),
        },
        {
            headerName: "Agent Sentiment",
            field: "agent_sentiment",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Agent Sentiment</div>
                    <div className="tooltip-description">Emotional tone of the agent during the call</div>
                    <div className="tooltip-range">Valid range: -1 (very negative) to 1 (very positive)</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            cellRenderer: (params) => (
                <div className="flex justify-center items-center w-full h-full py-2">
                    {getSentimentIcon(params.value)}
                </div>
            ),
            width: 85,
        },
        {
            headerName: "Customer Sentiment",
            field: "customer_sentiment",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Customer Sentiment</div>
                    <div className="tooltip-description">Emotional tone of the customer during the call</div>
                    <div className="tooltip-range">Valid range: -1 (very negative) to 1 (very positive)</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            cellRenderer: (params) => (
                <div className="flex justify-center items-center w-full h-full py-2">
                    {getSentimentIcon(params.value)}
                </div>
            ),
            width: 115,
        },
        {
            headerName: "Agent Performance",
            field: "agent_performance_score",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Agent Performance</div>
                    <div className="tooltip-description">How well the agent performed their job duties</div>
                    <div className="tooltip-range">Valid range: 0 (poor) to 1 (excellent)</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            cellRenderer: (params) => (
                <ScoreProgressBar
                    value={params.value}
                    reason={params.data.agent_performance_reason}
                    color="blue"
                />
            ),
            width: 120,
        },
        {
            headerName: "Customer Experience",
            field: "customer_experience_score",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Customer Experience</div>
                    <div className="tooltip-description">Quality of customer's overall experience during the call</div>
                    <div className="tooltip-range">Valid range: 0 (poor) to 1 (excellent)</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            cellRenderer: (params) => (
                <ScoreProgressBar
                    value={params.value}
                    reason={params.data.customer_experience_score_reason}
                    color="green"
                />
            ),
            width: 135,
        },
        {
            headerName: "Customer Satisfied?",
            field: "customer_final_satisfaction",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Customer Satisfaction</div>
                    <div className="tooltip-description">Whether the customer was satisfied at the end of the call</div>
                    <div className="tooltip-range">Valid range: 0 (very unsatisfied) to 1 (very satisfied)</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            cellRenderer: (params) =>
                getCustomerSatisfaction(
                    params.value,
                    params.data.customer_final_satisfaction_reason
                ),
            width: 157,
        },
        {
            headerName: "Summary",
            field: "summary",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Call Summary</div>
                    <div className="tooltip-description">Brief overview of the conversation</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            sortable: false,
            cellRenderer: (params) => (
                <Tippy
                    content={params.value}
                    className="custom-tooltip"
                    duration={[200, 400]}
                    delay={100}
                    maxWidth={400}
                >
                    <button className="underline text-usan-primary hover:text-usan-hover">
                        View
                    </button>
                </Tippy>
            ),
            width: 97,
        },
        {
            headerName: "Major Intents",
            field: "intents_agg_json",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Major Intents</div>
                    <div className="tooltip-description">Main reasons and topics of the call</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            flex: 1,
            minWidth: 200,
            sortable: false,
            cellRenderer: IntentCellRenderer,
        },
    ];
    const qmColumns = qualityManagementEnabled ? [
        {
            headerName: "QM Score",
            field: "qm_score",
            type: 'rightAligned',
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Quality Management Score</div>
                    <div className="tooltip-description">
                        Overall quality score for this interaction. Click to view scorecard.
                    </div>
                    <div className="tooltip-range">Valid range: 0 to 100</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            width: 100,
            cellRenderer: (params) => {
                const scoreExists = params.value != null && !isNaN(parseFloat(params.value));
                const contactId = params.data.contact_id;

                if (!scoreExists || !contactId) {
                    return <span className="text-gray-400">N/A</span>;
                }

                const score = Math.round(parseFloat(params.value));

                const handleContextMenu = (event) => {
                    event.preventDefault();

                    const existingMenu = document.getElementById("custom-menu");
                    if (existingMenu) {
                        document.body.removeChild(existingMenu);
                    }

                    const menu = document.createElement("div");
                    menu.id = "custom-menu";
                    menu.style.position = "absolute";
                    menu.style.left = `${event.pageX}px`;
                    menu.style.top = `${event.pageY}px`;
                    menu.style.border = "1px solid #ccc";
                    menu.style.padding = "8px";
                    menu.style.zIndex = 1000;
                    menu.style.backgroundColor = "white";
                    menu.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
                    menu.style.borderRadius = "4px";

                    const openInNewWindowOption = document.createElement("div");
                    openInNewWindowOption.textContent = "Open in New Window";
                    openInNewWindowOption.style.cursor = "pointer";
                    openInNewWindowOption.style.padding = "4px 8px";
                    openInNewWindowOption.onmouseover = () => {
                        openInNewWindowOption.style.backgroundColor = "#f3f4f6";
                    };
                    openInNewWindowOption.onmouseout = () => {
                        openInNewWindowOption.style.backgroundColor = "transparent";
                    };
                    openInNewWindowOption.onclick = () => {
                        window.open(`/scorecard/${contactId}`, "_blank");
                        if (menu.parentNode) {
                            menu.parentNode.removeChild(menu);
                        }
                    };

                    menu.appendChild(openInNewWindowOption);
                    document.body.appendChild(menu);

                    const handleClickOutside = (event) => {
                        if (menu && !menu.contains(event.target)) {
                            if (menu.parentNode) {
                                menu.parentNode.removeChild(menu);
                            }
                            document.removeEventListener("click", handleClickOutside);
                        }
                    };

                    document.addEventListener("click", handleClickOutside);
                };

                return (
                    <span
                        className="text-usan-primary hover:text-usan-primary cursor-pointer"
                        onClick={() => {
                            navigate(`/scorecard/${contactId}`, {
                                state: { filters: filters, referrer: location.pathname },
                            });
                        }}
                        onContextMenu={handleContextMenu}
                    >
                        {score}
                    </span>
                );
            },
        },
        {
            headerName: "QM Autofail",
            field: "is_autofail",
            tooltipContent: (
                <div>
                    <div className="tooltip-title">Quality Management Autofail</div>
                    <div className="tooltip-description">Critical conditions that result in automatic failure</div>
                </div>
            ),
            headerComponent: CustomHeaderComponent,
            cellRenderer: QMAutofailRenderer,
            width: 120,
        }
    ] : [];

    let columnDefs;
    if (qualityManagementEnabled) {
        // Separate the summary/intents columns from the other base columns
        const summaryAndIntentsCols = baseColumnDefs.filter(
            (c) => c.field === "summary" || c.field === "intents_agg_json"
        );
        const otherBaseCols = baseColumnDefs.filter(
            (c) => c.field !== "summary" && c.field !== "intents_agg_json"
        );

        // Reassemble in the desired order: other base columns, qm columns, then summary/intents
        columnDefs = [...otherBaseCols, ...qmColumns, ...summaryAndIntentsCols];
    } else {
        // If QM is not enabled, use the original order
        columnDefs = [...baseColumnDefs, ...qmColumns];
    }

    // Filter out fields if specified
    columnDefs = columnDefs.filter((c) => !hiddenFields.includes(c.field));

    // Override widths if specified
    columnDefs = columnDefs.map((c) => {
        const newColumnDef = c;
        if (newColumnDef.field in widthOverrides) {
            newColumnDef.width = widthOverrides[newColumnDef.field];
        }
        return newColumnDef;
    })

    const pageSize = 20;

    const renderPageNumbers = () => {
        const pageNumbers = [];
        const totalPageNumbers = 9; // Total number of page elements to show

        if (totalPages <= totalPageNumbers) {
            // If total pages are less than or equal to totalPageNumbers, show all pages
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(
                    <PageButton
                        key={i}
                        page={i}
                        currentPage={currentPage}
                        onClick={() => onPageChange(i)}
                    />
                );
            }
        } else {
            // Always show first page
            pageNumbers.push(
                <PageButton
                    key={1}
                    page={1}
                    currentPage={currentPage}
                    onClick={() => onPageChange(1)}
                />
            );

            // Calculate start and end of middle section
            let startPage = Math.max(2, currentPage - 2);
            let endPage = Math.min(totalPages - 1, currentPage + 2);

            // Adjust if at the start or end
            if (currentPage <= 3) {
                endPage = Math.min(totalPages - 1, 5);
            }
            if (endPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 4);
            }

            // Add ellipsis if needed at the start
            if (startPage > 2) {
                pageNumbers.push(<EllipsisButton key="ellipsis-start" />);
            }

            // Add middle pages
            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(
                    <PageButton
                        key={i}
                        page={i}
                        currentPage={currentPage}
                        onClick={() => onPageChange(i)}
                    />
                );
            }

            // Add ellipsis if needed at the end
            if (endPage < totalPages - 1) {
                pageNumbers.push(<EllipsisButton key="ellipsis-end" />);
            }

            // Always show last page
            pageNumbers.push(
                <PageButton
                    key={totalPages}
                    page={totalPages}
                    currentPage={currentPage}
                    onClick={() => onPageChange(totalPages)}
                />
            );
        }

        return pageNumbers;
    };

    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            resizable: true,
        }),
        []
    );

    const onGridReady = useCallback(
        (params) => {
            console.log("The grid is now ready", params);
            gridApiRef.current = params.api;
            if (sortModel && sortModel.length > 0) {
                params.api.applyColumnState({
                    state: sortModel.map((model) => ({
                        colId: model.colId,
                        sort: model.sort,
                    })),
                    defaultState: { sort: null },
                });
            }
        },
        [sortModel]
    );

    const onSortChanged = useCallback(
        (event) => {
            if (isUpdatingRef.current) return;

            const newSortModel = event.api
                .getColumnState()
                .filter((column) => column.sort)
                .map((column) => ({
                    colId: column.colId,
                    sort: column.sort,
                }));

            if (
                JSON.stringify(newSortModel) !==
                JSON.stringify(currentSortModelRef.current)
            ) {
                currentSortModelRef.current = newSortModel;
                onSortChange(newSortModel);
            }
        },
        [onSortChange]
    );

    useEffect(() => {
        if (
            gridApiRef.current &&
            JSON.stringify(sortModel) !== JSON.stringify(currentSortModelRef.current)
        ) {
            isUpdatingRef.current = true;
            gridApiRef.current.applyColumnState({
                state: sortModel.map((model) => ({
                    colId: model.colId,
                    sort: model.sort,
                })),
                defaultState: { sort: null },
            });
            currentSortModelRef.current = sortModel;
            isUpdatingRef.current = false;
        }
    }, [sortModel]);

    // Consolidated export function
    const buildExportParams = (filters) => {
        const queryParams = new URLSearchParams();
        
        if (filters.category) {
            queryParams.append("category", filters.category.value);
        }
        if (filters.intent) {
            queryParams.append("intent", filters.intent.value);
        }
        if (selectedAgent) {       
            queryParams.append("agent", selectedAgent);
        } else if (filters.agent) {
            let agentValue = filters.agent;
            if (agentValue.value) {
                agentValue = agentValue.value;
            }
            queryParams.append("agent", agentValue);
        }
        if (filters.callId) {
            queryParams.append("callId", filters.callId);
        }
        if (filters.dateRange) {
            queryParams.append("dateRange", filters.dateRange.value);
        }
        if (filters.dateStart) {
            queryParams.append("dateStart", formatDateToYYYYMMDD(filters.dateStart));
        }
        if (filters.dateEnd) {
            queryParams.append("dateEnd", formatDateToYYYYMMDD(filters.dateEnd));
        }
        if (selectedIntent) {
            console.log(`Selected intent: ${selectedIntent}`);
            queryParams.append("minorIntents", selectedIntent);
        } else if (filters.minorIntents) {
            if (Array.isArray(filters.minorIntents)) {
                filters.minorIntents.forEach((intent) =>
                    queryParams.append("minorIntents", intent.value)
                );
            } else {
                queryParams.append("minorIntents", filters.minorIntents);
            }
        }
        if (filters.queue && filters.queue.length > 0) {
            filters.queue.forEach((queue) => {
                queryParams.append("queue", queue.value);
            });
        }
        if (filters.initiationMethod && filters.initiationMethod.length > 0) {
            filters.initiationMethod.forEach((initiationMethod) => {
                queryParams.append("initiationMethod", initiationMethod.value);
            });
        }
        if (!filters.excludeWeekends) {
            queryParams.append("excludeWeekends", filters.excludeWeekends);
        }
        if (isQualityMangament) {
            queryParams.append("qmOnly", true);
        }
        
        return queryParams;
    };

    const handleExport = async (format) => {
        setIsExporting(true);
        try {
            console.log(`Starting ${format.toUpperCase()} export with filters:`, filters);
            
            const queryParams = buildExportParams(filters);
            const action = format === 'csv' ? 'export-csv' : 'export-excel';
            
            // Convert URLSearchParams to object for sendRequest
            const paramsObject = {};
            for (const [key, value] of queryParams.entries()) {
                if (paramsObject[key]) {
                    // Handle multiple values for same key
                    if (!Array.isArray(paramsObject[key])) {
                        paramsObject[key] = [paramsObject[key]];
                    }
                    paramsObject[key].push(value);
                } else {
                    paramsObject[key] = value;
                }
            }
            
            const result = await Tools.downloadFromPresignedUrl(action, paramsObject);
            console.log(`Successfully exported ${result.recordCount} records to ${format.toUpperCase()}`);           
            
        } catch (error) {
            console.error(`${format.toUpperCase()} export failed:`, error);
            alert(`Export failed. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportCSV = () => handleExport('csv');
    const handleExportExcel = () => handleExport('excel');

    return (
    <div className="ag-theme-alpine" style={{ width: "100%", margin: 0 }}>
        <AgGridReact
            rowData={data}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            defaultColDef={defaultColDef}
            onSortChanged={onSortChanged}
            sortingOrder={["asc", "desc"]}
            serverSideSorting={true}
            onGridReady={onGridReady}
            enableCellTextSelection={true}
            ensureDomOrder={true}
            loadingOverlayComponent="Loading..."
            overlayLoadingTemplate={
                '<span class="ag-overlay-loading-center">Loading data...</span>'
            }
            rowClass="hover:bg-gray-50"
        />

        {/* Modified pagination toolbar with 3 sections */}
            <div className="flex justify-between items-center mt-6 bg-white p-4 rounded-lg shadow">
                {/* Left: Record count */}
                <div className="text-sm text-gray-600 flex-1">
                    Showing {data.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
                    {Math.min(currentPage * pageSize, totalRecs)} of {totalRecs} records
                </div>
                
                {/* Middle: Export buttons */}
                <div className="flex items-center space-x-3 mx-8">
                    <button
                        className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleExportCSV}
                        disabled={isExporting}
                    >
                        <span className="mr-2">📊</span>
                        Download as CSV
                    </button>
                    
                    <button
                        className="inline-flex items-center px-3 py-1.5 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleExportExcel}
                        disabled={isExporting}
                    >
                        <span className="mr-2">📈</span>
                        Download as Excel
                    </button>
                </div>

                {/* Loading Mask */}
                {isExporting && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-8 shadow-lg flex flex-col items-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                            <p className="text-lg font-medium text-gray-700">File is being created...</p>
                        </div>
                    </div>
                )}
                
                {/* Right: Pagination controls */}
                <div className="flex items-center space-x-2 flex-1 justify-end">
                    <button
                        className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={currentPage === 1}
                        onClick={() => onPageChange(currentPage - 1)}
                        aria-label="Previous page"
                    >
                        <FaChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center space-x-1">
                        {renderPageNumbers()}
                    </div>
                    <button
                        className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={currentPage === totalPages}
                        onClick={() => onPageChange(+currentPage + 1)}
                        aria-label="Next page"
                    >
                        <FaChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

const PageButton = ({ page, currentPage, onClick }) => (
    <button
        className={`px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${page === currentPage
            ? "bg-blue-600 text-white"
            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
        onClick={onClick}
        aria-label={`Go to page ${page}`}
        aria-current={page === currentPage ? "page" : undefined}
    >
        {page}
    </button>
);

const EllipsisButton = () => (
    <button className="px-3 py-1" disabled aria-hidden="true">
        <FaEllipsisH className="w-5 h-5 text-gray-400" />
    </button>
);

export default MainGrid;

function formatDateToYYYYMMDD(date) {
  const d = new Date(date);
  let month = "" + (d.getMonth() + 1);
  let day = "" + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
}